import {
    Bot, Phone, Shield, BarChart3, Clock, DollarSign,
    Webhook, Brain, Globe, Layers, Mic, Settings,
    ArrowRight, Cpu, Database, Lock, Sparkles,
    Headphones, MessageSquare, RefreshCw, Sliders
} from 'lucide-react';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'http://localhost:3200';
interface FeatureItem {
    icon: typeof Bot;
    title: string;
    desc: string;
}

interface FeatureSection {
    id: string;
    badge: string;
    title: string;
    subtitle: string;
    gradientClass: string;
    features: FeatureItem[];
}

const FEATURE_SECTIONS: FeatureSection[] = [
    {
        id: 'agents',
        badge: 'Core Platform',
        title: 'Autonomous Voice Agents',
        subtitle: 'Deploy AI agents that handle complete call workflows without human intervention.',
        gradientClass: 'grad-nova-plasma',
        features: [
            { icon: Bot, title: 'Multi-Engine Support', desc: 'Standard, Realtime, Pro, and Personaplex engines — choose the right model for every use case.' },
            { icon: Mic, title: 'Natural Voice Selection', desc: '50+ premium voices across providers. A/B test voices to maximize conversion rates.' },
            { icon: Brain, title: 'Dynamic Prompting', desc: 'Context-aware prompts that adapt based on caller intent, time of day, and history.' },
            { icon: Settings, title: 'Tool Calling', desc: 'Agents call your APIs mid-conversation — check calendars, create bookings, query CRMs.' },
        ],
    },
    {
        id: 'telephony',
        badge: 'Infrastructure',
        title: 'Multi-Provider Telephony',
        subtitle: 'Enterprise-grade call handling with sub-second latency.',
        gradientClass: 'grad-plasma',
        features: [
            { icon: Phone, title: 'Inbound & Outbound', desc: 'Both directions fully supported. Transfer, warm handoff, or conference with live agents.' },
            { icon: Layers, title: 'Provider Abstraction', desc: 'Switch between Retell, Twilio, or Vonage without code changes. Hot-swap in production.' },
            { icon: Clock, title: '<500ms E2E Latency', desc: 'Optimized STT→LLM→TTS pipeline. Feels like talking to a human, not a machine.' },
            { icon: RefreshCw, title: 'Automatic Failover', desc: 'If one provider drops, calls route to backup automatically. Zero downtime.' },
        ],
    },
    {
        id: 'analytics',
        badge: 'Intelligence',
        title: 'Real-time Analytics & Insights',
        subtitle: 'Deep visibility into every call, agent, and dollar spent.',
        gradientClass: 'grad-solar-ember',
        features: [
            { icon: BarChart3, title: 'Live Dashboards', desc: 'Active call monitoring, conversion funnels, and trend analysis — all updating in real-time.' },
            { icon: DollarSign, title: 'Cost Tracking', desc: 'Per-minute billing breakdown by engine, channel, and agent. Set alerts before budgets are hit.' },
            { icon: MessageSquare, title: 'Transcript Analysis', desc: 'Full transcripts indexed and searchable. Spot patterns, extract insights, improve prompts.' },
            { icon: Sparkles, title: 'AI Recommendations', desc: 'Machine learning suggests prompt tweaks, voice changes, and workflow optimizations.' },
        ],
    },
    {
        id: 'compliance',
        badge: 'Trust & Security',
        title: 'Compliance Built In',
        subtitle: 'CASL, TCPA, and PIPEDA compliance without the headache.',
        gradientClass: 'grad-pulse-plasma',
        features: [
            { icon: Shield, title: 'Consent Management', desc: 'Track express and implied consent per contact. Auto-expire and audit every interaction.' },
            { icon: Lock, title: 'DNC Integration', desc: 'Real-time checks against national and internal Do-Not-Call lists before every dial.' },
            { icon: Database, title: 'Audit Trails', desc: 'Immutable logs for every call, consent change, and data access. Ready for regulators.' },
            { icon: Globe, title: 'Multi-Province', desc: 'Province-specific rules for CASL, with automatic jurisdiction detection from phone numbers.' },
        ],
    },
    {
        id: 'integrations',
        badge: 'Extensibility',
        title: 'Integrations & API',
        subtitle: 'Connect Lumina to your existing stack in minutes.',
        gradientClass: 'grad-nova-glow',
        features: [
            { icon: Webhook, title: 'Webhooks', desc: 'Real-time event notifications for call starts, ends, bookings, and transcript completions.' },
            { icon: Cpu, title: 'REST API', desc: 'Full CRUD for agents, calls, contacts, and analytics. Every feature accessible via API.' },
            { icon: Headphones, title: 'CRM Connectors', desc: 'Pre-built integrations for Salesforce, HubSpot, Noterro, and more. Custom SSO via SAML.' },
            { icon: Sliders, title: 'Custom Tools', desc: 'Define your own function tools for agents to call during conversations. No limits.' },
        ],
    },
];

export default function Features() {
    return (
        <div style={{ paddingTop: '7rem', paddingBottom: '4rem' }}>
            {/* Header */}
            <section className="section-container text-center" style={{ marginBottom: '4rem' }}>
                <div
                    className="glass inline-flex items-center"
                    style={{ padding: '0.375rem 0.75rem', borderRadius: '999px', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '1.5rem' }}
                >
                    <Sparkles style={{ width: 12, height: 12, color: 'var(--color-nova)' }} />
                    <span className="text-stardust">Full Platform Overview</span>
                </div>
                <h1
                    className="font-display font-extrabold text-starlight"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', lineHeight: 1.15 }}
                >
                    One platform.{' '}
                    <span className="gradient-text">Every capability.</span>
                </h1>
                <p className="text-stardust" style={{ fontSize: '1.125rem', opacity: 0.7, maxWidth: '640px', margin: '0 auto' }}>
                    Everything you need to build, deploy, and scale autonomous voice agents —
                    with enterprise compliance and real-time analytics built in.
                </p>
            </section>

            {/* Feature Sections */}
            {FEATURE_SECTIONS.map((section, sectionIdx) => (
                <section
                    key={section.id}
                    id={section.id}
                    className={sectionIdx % 2 === 1 ? 'mesh-gradient' : ''}
                    style={{ padding: '5rem 0' }}
                >
                    <div className="section-container">
                        <div style={{ maxWidth: '640px', marginBottom: '2.5rem' }}>
                            <span
                                className="gradient-text font-semibold"
                                style={{ fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'inline-block', marginBottom: '0.75rem' }}
                            >
                                {section.badge}
                            </span>
                            <h2
                                className="font-display font-bold text-starlight"
                                style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', marginBottom: '0.75rem' }}
                            >
                                {section.title}
                            </h2>
                            <p className="text-stardust" style={{ opacity: 0.7 }}>
                                {section.subtitle}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '1.25rem' }}>
                            {section.features.map((feat, i) => (
                                <div
                                    key={i}
                                    className="glass glass-hover group animate-fade-in-up"
                                    style={{
                                        borderRadius: '16px',
                                        padding: '1.5rem',
                                        display: 'flex',
                                        gap: '1rem',
                                        animationDelay: `${i * 100}ms`,
                                        transition: 'transform 300ms',
                                    }}
                                >
                                    <div className={`icon-box ${section.gradientClass}`} style={{ flexShrink: 0 }}>
                                        <feat.icon style={{ width: 20, height: 20, color: 'white' }} />
                                    </div>
                                    <div>
                                        <h3 className="font-display font-semibold text-starlight" style={{ marginBottom: '0.25rem' }}>
                                            {feat.title}
                                        </h3>
                                        <p className="text-stardust" style={{ fontSize: '0.8125rem', lineHeight: 1.6, opacity: 0.6 }}>
                                            {feat.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            {/* Bottom CTA */}
            <section className="relative overflow-hidden" style={{ padding: '5rem 0' }}>
                <div className="aurora aurora-1" style={{ opacity: 0.15 }} />
                <div className="section-container text-center relative z-10" style={{ maxWidth: '800px' }}>
                    <h2
                        className="font-display font-bold text-starlight"
                        style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '1rem' }}
                    >
                        See it in action
                    </h2>
                    <p className="text-stardust" style={{ opacity: 0.7, marginBottom: '2rem' }}>
                        Try the live demo on our homepage or start your free trial — no credit card required.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a href={`${PORTAL_URL}/signup`} className="btn-primary glow-cta" style={{ padding: '0.875rem 2rem' }}>
                            Start Free Trial
                            <ArrowRight style={{ width: 16, height: 16 }} />
                        </a>
                        <a href="/" className="btn-secondary" style={{ padding: '0.875rem 2rem' }}>
                            <Phone style={{ width: 16, height: 16 }} />
                            Try Live Demo
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
